import { useState } from "react";
import { Check, ChevronDown, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { brandsFor, findBrand } from "@/lib/brands";
import { cn } from "@/lib/utils";

export const BrandLogo = ({ slug, name, className = "h-6 w-6" }) => (
  <span className={cn("flex shrink-0 items-center justify-center overflow-hidden rounded-md bg-white", className)}>
    {slug ? <img src={`/brands/${slug}.png`} alt="" className="h-full w-full object-contain p-0.5" onError={(e) => { e.currentTarget.style.display = "none"; }} />
      : <span className="text-[10px] font-bold text-navy">{(name || "?")[0]}</span>}
  </span>
);

const Picker = ({ value, placeholder, testid, children, open, setOpen, display }) => (
  <Popover open={open} onOpenChange={setOpen}>
    <PopoverTrigger asChild>
      <button type="button" className="flex h-10 w-full items-center gap-2 rounded-md border border-input bg-background px-3 text-left text-sm" data-testid={testid}>
        {display}
        <span className={cn("min-w-0 flex-1 truncate", !value && "text-muted-foreground")}>{value || placeholder}</span>
        <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
      </button>
    </PopoverTrigger>
    <PopoverContent className="w-72 p-2" align="start">{children}</PopoverContent>
  </Popover>
);

const Option = ({ active, onClick, children, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid} className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-secondary">
    {children}<span className="flex-1" />{active && <Check className="h-4 w-4 text-sage" />}
  </button>
);

export const BrandModelFields = ({ category, brand, model, onChange }) => {
  const [openB, setOpenB] = useState(false);
  const [openM, setOpenM] = useState(false);
  const [q, setQ] = useState("");
  const [otherBrand, setOtherBrand] = useState(!!brand && !findBrand(category, brand));
  const known = findBrand(category, brand);
  const [otherModel, setOtherModel] = useState(!!model && !(known?.models || []).includes(model));
  const list = brandsFor(category).filter((b) => b.name.toLowerCase().includes(q.toLowerCase()));

  const pickBrand = (b) => { setOtherBrand(!b); setOtherModel(false); onChange({ brand: b ? b.name : "", model: "" }); setOpenB(false); setQ(""); };
  return (
    <>
      <div>
        <div className="mb-1.5 text-sm font-medium">Marque</div>
        <Picker value={otherBrand ? "Autre" : known?.name || brand} placeholder="Choisir" testid="asset-brand-picker" open={openB} setOpen={setOpenB}
          display={known && <BrandLogo slug={known.slug} name={known.name} />}>
          <div className="relative mb-2"><Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher" className="h-9 pl-8" data-testid="asset-brand-search" /></div>
          <div className="max-h-64 overflow-y-auto">
            {list.map((b) => <Option key={b.slug} active={known?.slug === b.slug} onClick={() => pickBrand(b)} testid={`brand-option-${b.slug}`}><BrandLogo slug={b.slug} name={b.name} />{b.name}</Option>)}
            <Option active={otherBrand} onClick={() => pickBrand(null)} testid="brand-option-other"><BrandLogo name="+" />Autre marque</Option>
          </div>
        </Picker>
        {otherBrand && <Input className="mt-2" value={brand || ""} onChange={(e) => onChange({ brand: e.target.value })} placeholder="Nom de la marque" data-testid="asset-brand-input" />}
      </div>
      <div>
        <div className="mb-1.5 text-sm font-medium">Modèle</div>
        {known && known.models.length ? (
          <Picker value={otherModel ? "Autre" : model} placeholder="Choisir" testid="asset-model-picker" open={openM} setOpen={setOpenM}>
            <div className="max-h-64 overflow-y-auto">
              {known.models.map((m) => <Option key={m} active={model === m && !otherModel} onClick={() => { setOtherModel(false); onChange({ model: m }); setOpenM(false); }} testid={`model-option-${m}`}>{m}</Option>)}
              <Option active={otherModel} onClick={() => { setOtherModel(true); onChange({ model: "" }); setOpenM(false); }} testid="model-option-other">Autre modèle</Option>
            </div>
          </Picker>
        ) : null}
        {(!known || !known.models.length || otherModel) && <Input className={known?.models.length ? "mt-2" : ""} value={model || ""} onChange={(e) => onChange({ model: e.target.value })} placeholder="Référence du modèle" data-testid="asset-model-input" />}
      </div>
    </>
  );
};
